/**
 * Amplitude Event Helpers
 *
 * These functions return EVENT PROPERTIES (data specific to each event)
 * Pass them to amplitude.track(eventName, eventProperties)
 */

import {
  LAUNCH_LEGACY_LOCATION_CREATE,
  LAUNCH_LEGACY_LOCATION_EDIT,
  LAUNCH_LEGACY_LOCATION_FORMNAME
} from '../constants/amplitude'

import {
  AMPLITUDE_EVENT_ENTRY_DELETED,
  AMPLITUDE_EVENT_ENTRIES_DELETED,
  AMPLITUDE_EVENT_ENTRY_DETAILS_OPENED,
  AMPLITUDE_EVENT_ENTRY_DETAILS_NEXT_PREV_CLICKED,
  AMPLITUDE_EVENT_ENTRY_PRINTED,
  AMPLITUDE_EVENT_ENTRY_MANAGER_OPENED,
  AMPLITUDE_EVENT_ENTRY_MANAGER_FILTER_APPLIED,
  AMPLITUDE_EVENT_ENTRY_MANAGER_FILTER_CREATED,
  AMPLITUDE_EVENT_ENTRY_MANAGER_COLUMNS_MODIFIED,
  AMPLITUDE_EVENT_FORM_MANAGER_LAUNCHED,
  AMPLITUDE_EVENT_FORM_MANAGER_ACTION_PERFORMED,
  AMPLITUDE_EVENT_FORM_MANAGER_BULK_ACTION_CLICKED_IN_DRAWER,
  AMPLITUDE_EVENT_FORM_MANAGER_DUPLICATE_FORM,
  AMPLITUDE_EVENT_FORM_MANAGER_FORM_RENAME,
  AMPLITUDE_EVENT_FORM_MANAGER_VIEW_FORM,
  AMPLITUDE_EVENT_FORM_MANAGER_SEARCH,
  AMPLITUDE_EVENT_FORM_MANAGER_FILTER_BY_LABELS,
  AMPLITUDE_EVENT_FORM_MANAGER_SORT,
  AMPLITUDE_EVENT_FORM_MANAGER_PAGE_TURNED,
  AMPLITUDE_EVENT_FORM_BUILDER_LAUNCHED,
  AMPLITUDE_EVENT_UPGRADE_CTA_CLICKED,
  AMPLITUDE_EVENT_PROPERTY_FORM_TYPE,
  AMPLITUDE_EVENT_PROPERTY_NUM_FORMS_SELECTED,
  AMPLITUDE_EVENT_PROPERTY_ALL_FORMS_SELECTED,
  AMPLITUDE_EVENT_PROPERTY_ACTION_PERFORMED,
  AMPLITUDE_EVENT_PROPERTY_FORM_TYPE_VIEW,
  AMPLITUDE_EVENT_PROPERTY_SOURCE_FORM_ID,
  AMPLITUDE_EVENT_PROPERTY_TOTAL_NUM_FORMS,
  AMPLITUDE_EVENT_PROPERTY_FORM_ID,
  AMPLITUDE_EVENT_PROPERTY_ENTRY_MANAGER_VERSION,
  AMPLITUDE_EVENT_PROPERTY_NUM_ENTRIES,
  AMPLITUDE_EVENT_PROPERTY_NUM_ENTRIES_DELETED,
  AMPLITUDE_EVENT_PROPERTY_ALL_ENTRIES_SELECTED,
  AMPLITUDE_EVENT_PROPERTY_ENTRY_ID,
  AMPLITUDE_EVENT_PROPERTY_ENTRY_CREATED_DATE,
  AMPLITUDE_EVENT_PROPERTY_DIRECTION,
  AMPLITUDE_EVENT_PROPERTY_NUM_COLUMNS_SELECTED,
  AMPLITUDE_EVENT_PROPERTY_FILTERED_BY_METADATA,
  AMPLITUDE_EVENT_PROPERTY_IS_FILTERED_BY_FORM_FIELDS,
  AMPLITUDE_EVENT_PROPERTY_PRE_BUILT_FILTER_USED,
  AMPLITUDE_EVENT_PROPERTY_NUM_ARGUMENTS,
  AMPLITUDE_EVENT_PROPERTY_NUM_ENTRIES_RETURNED,
  AMPLITUDE_EVENT_PROPERTY_ENTRIES_PER_PAGE,
  AMPLITUDE_EVENT_PROPERTY_FILTER_APPLIED,
  AMPLITUDE_EVENT_PROPERTY_SORT_BY,
  AMPLITUDE_EVENT_PROPERTY_SORT_ORDER,
  AMPLITUDE_EVENT_PROPERTY_NUM_QUESTIONS,
  AMPLITUDE_EVENT_PROPERTY_SORT_BY_PROP,
  AMPLITUDE_EVENT_PROPERTY_SORT_ORDER_PROP,
  AMPLITUDE_EVENT_PROPERTY_FILTER_PRE_APPLIED,
  AMPLITUDE_EVENT_PROPERTY_SEARCH_PRE_APPLIED,
  AMPLITUDE_EVENT_PROPERTY_FORMS_PER_PAGE,
  AMPLITUDE_EVENT_PROPERTY_TOTAL_NUM_FORMS_IN_ACCOUNT,
  AMPLITUDE_EVENT_PROPERTY_ON_LOGIN,
  AMPLITUDE_EVENT_PROPERTY_ACTION_INVOKED_LOCATION,
  AMPLITUDE_EVENT_PROPERTY_PURPOSE,
  AMPLITUDE_EVENT_PROPERTY_PASSWORD_REQUIRED,
  AMPLITUDE_EVENT_PROPERTY_CREATE_FORM,
  AMPLITUDE_EVENT_PROPERTY_SEARCH_TYPE,
  AMPLITUDE_EVENT_PROPERTY_NUM_FORMS_RETURNED,
  AMPLITUDE_EVENT_PROPERTY_HAS_FILTER,
  AMPLITUDE_EVENT_PROPERTY_NUM_LABELS_APPLIED,
  AMPLITUDE_EVENT_PROPERTY_SEARCH_APPLIED,
  AMPLITUDE_EVENT_PROPERTY_VISIBILITY,
  AMPLITUDE_EVENT_PROPERTY_OLD_SORT_BY,
  AMPLITUDE_EVENT_PROPERTY_NEW_SORT_BY,
  AMPLITUDE_EVENT_PROPERTY_OLD_SORT_ORDER,
  AMPLITUDE_EVENT_PROPERTY_NEW_SORT_ORDER,
  AMPLITUDE_EVENT_PROPERTY_PAGE_TURNED_TYPE,
  AMPLITUDE_EVENT_PROPERTY_NUM_PAGES,
  AMPLITUDE_EVENT_PROPERTY_CTA_NAME,
} from '../constants/amplitudeEvents'


const bulkActionsProps = (action, formNum, selectedFormNum) => {
  const props = {
    [AMPLITUDE_EVENT_PROPERTY_FORM_TYPE]: 'Legacy Forms',
    [AMPLITUDE_EVENT_PROPERTY_NUM_FORMS_SELECTED]: selectedFormNum,
    [AMPLITUDE_EVENT_PROPERTY_ALL_FORMS_SELECTED]: selectedFormNum === formNum,
    [AMPLITUDE_EVENT_PROPERTY_ACTION_PERFORMED]: action // Delete or assign labels
  }
  return props
}

export const getActionPerformedEventDetails = (action, { formNum, selectedFormNum }) => {
  return {
    event: AMPLITUDE_EVENT_FORM_MANAGER_ACTION_PERFORMED,
    properties: bulkActionsProps(action, formNum, selectedFormNum)
  }
}

export const getBulkActionDrawerEventDetails = (action, { formNum, selectedFormNum }) => {
  return {
    event: AMPLITUDE_EVENT_FORM_MANAGER_BULK_ACTION_CLICKED_IN_DRAWER,
    properties: bulkActionsProps(action, formNum, selectedFormNum)
  }
}

export const getDuplicateFormEventDetails = (formId, { formNum }) => {
  return {
    event: AMPLITUDE_EVENT_FORM_MANAGER_DUPLICATE_FORM,
    properties: {
      [AMPLITUDE_EVENT_PROPERTY_FORM_TYPE_VIEW]: 'Legacy Forms',
      [AMPLITUDE_EVENT_PROPERTY_SOURCE_FORM_ID]: formId,
      [AMPLITUDE_EVENT_PROPERTY_TOTAL_NUM_FORMS]: formNum
    }
  }
}

export const getEntriesDeletedEventDetails = (formId, { isSelectedAll, numOfEntries, numOfEntriesDeleted }) => {
  return {
    event: AMPLITUDE_EVENT_ENTRIES_DELETED,
    properties: {
      [AMPLITUDE_EVENT_PROPERTY_FORM_ID]: formId,
      [AMPLITUDE_EVENT_PROPERTY_ENTRY_MANAGER_VERSION]: 'Classic Forms',
      [AMPLITUDE_EVENT_PROPERTY_NUM_ENTRIES]: numOfEntries,
      [AMPLITUDE_EVENT_PROPERTY_NUM_ENTRIES_DELETED]: numOfEntriesDeleted,
      [AMPLITUDE_EVENT_PROPERTY_ALL_ENTRIES_SELECTED]: isSelectedAll,
    }
  }
}

export const getEntryDeletedEventDetails = (formId, { entryId, numOfEntries }) => {
  return {
    event: AMPLITUDE_EVENT_ENTRY_DELETED,
    properties: {
      [AMPLITUDE_EVENT_PROPERTY_FORM_ID]: formId,
      [AMPLITUDE_EVENT_PROPERTY_ENTRY_ID]: entryId,
      [AMPLITUDE_EVENT_PROPERTY_ENTRY_MANAGER_VERSION]: 'Classic Forms',
      [AMPLITUDE_EVENT_PROPERTY_NUM_ENTRIES]: numOfEntries
    }
  }
}

export const getEntryDetailsOpenedEventDetails = (formId, { dateCreated, entryId, numOfEntries }) => {
  return {
    event: AMPLITUDE_EVENT_ENTRY_DETAILS_OPENED,
    properties: {
      [AMPLITUDE_EVENT_PROPERTY_FORM_ID]: formId,
      [AMPLITUDE_EVENT_PROPERTY_ENTRY_CREATED_DATE]: dateCreated,
      [AMPLITUDE_EVENT_PROPERTY_ENTRY_ID]: entryId,
      [AMPLITUDE_EVENT_PROPERTY_ENTRY_MANAGER_VERSION]: 'Classic Forms',
      [AMPLITUDE_EVENT_PROPERTY_NUM_ENTRIES]: numOfEntries
    }
  }
}

export const getEntryDetailsNextPrevClicked = (formId, { direction, numOfEntries }) => {
  return {
    event: AMPLITUDE_EVENT_ENTRY_DETAILS_NEXT_PREV_CLICKED,
    properties: {
      [AMPLITUDE_EVENT_PROPERTY_FORM_ID]: formId,
      [AMPLITUDE_EVENT_PROPERTY_ENTRY_MANAGER_VERSION]: 'Classic Forms',
      [AMPLITUDE_EVENT_PROPERTY_DIRECTION]: direction,
      [AMPLITUDE_EVENT_PROPERTY_NUM_ENTRIES]: numOfEntries
    }
  }
}

export const getEntryManagerColumnsModified = (formId, { numOfSelectedColumns }) => {
  return {
    event: AMPLITUDE_EVENT_ENTRY_MANAGER_COLUMNS_MODIFIED,
    properties: {
      [AMPLITUDE_EVENT_PROPERTY_FORM_ID]: formId,
      [AMPLITUDE_EVENT_PROPERTY_ENTRY_MANAGER_VERSION]: 'Classic Forms',
      [AMPLITUDE_EVENT_PROPERTY_NUM_COLUMNS_SELECTED]: numOfSelectedColumns
    }
  }
}

export const getEntryManagerFilterApplied = (formId, { isFilteredByFormFields, metadataFields,
  numOfEntries, numOfEntriesReturned, numOfFilterArguments, preBuiltFilter }) => {
  return {
    event: AMPLITUDE_EVENT_ENTRY_MANAGER_FILTER_APPLIED,
    properties: {
      [AMPLITUDE_EVENT_PROPERTY_FORM_ID]: formId,
      [AMPLITUDE_EVENT_PROPERTY_ENTRY_MANAGER_VERSION]: 'Classic Forms',
      [AMPLITUDE_EVENT_PROPERTY_FILTERED_BY_METADATA]: metadataFields,
      [AMPLITUDE_EVENT_PROPERTY_IS_FILTERED_BY_FORM_FIELDS]: isFilteredByFormFields,
      [AMPLITUDE_EVENT_PROPERTY_PRE_BUILT_FILTER_USED]: preBuiltFilter, // all, today, expression
      [AMPLITUDE_EVENT_PROPERTY_NUM_ARGUMENTS]: numOfFilterArguments,
      [AMPLITUDE_EVENT_PROPERTY_NUM_ENTRIES]: numOfEntries,
      [AMPLITUDE_EVENT_PROPERTY_NUM_ENTRIES_RETURNED]: numOfEntriesReturned
    }
  }
}

export const getEntryManagerFilterCreated = (formId, {
  isFilteredByFormFields, metadataFields, numOfEntries, numOfFilterArguments }) => {
  return {
    event: AMPLITUDE_EVENT_ENTRY_MANAGER_FILTER_CREATED,
    properties: {
      [AMPLITUDE_EVENT_PROPERTY_FORM_ID]: formId,
      [AMPLITUDE_EVENT_PROPERTY_ENTRY_MANAGER_VERSION]: 'Classic Forms',
      [AMPLITUDE_EVENT_PROPERTY_NUM_ENTRIES]: numOfEntries,
      [AMPLITUDE_EVENT_PROPERTY_FILTERED_BY_METADATA]: metadataFields,
      [AMPLITUDE_EVENT_PROPERTY_IS_FILTERED_BY_FORM_FIELDS]: isFilteredByFormFields,
      [AMPLITUDE_EVENT_PROPERTY_NUM_ARGUMENTS]: numOfFilterArguments
    }
  }
}

export const getEntryManagerOpenedEventDetails = (formId, {
  entriesPerPage, filterApplied, numOfEntries, numOfSelectedColumns, sortDirection, sortBy }) => {
  return {
    event: AMPLITUDE_EVENT_ENTRY_MANAGER_OPENED,
    properties: {
      [AMPLITUDE_EVENT_PROPERTY_FORM_ID]: formId,
      [AMPLITUDE_EVENT_PROPERTY_ENTRY_MANAGER_VERSION]: 'Classic Forms',
      [AMPLITUDE_EVENT_PROPERTY_ENTRIES_PER_PAGE]: entriesPerPage,
      [AMPLITUDE_EVENT_PROPERTY_FILTER_APPLIED]: filterApplied, // all, today, expression
      [AMPLITUDE_EVENT_PROPERTY_SORT_BY]: sortBy,
      [AMPLITUDE_EVENT_PROPERTY_SORT_ORDER]: sortDirection,
      [AMPLITUDE_EVENT_PROPERTY_NUM_ENTRIES]: numOfEntries,
      [AMPLITUDE_EVENT_PROPERTY_NUM_COLUMNS_SELECTED]: numOfSelectedColumns
    }
  }
}

export const getEntryPrintedEventDetails = (formId, { entryId, numOfEntries, numOfQuestions }) => {
  return {
    event: AMPLITUDE_EVENT_ENTRY_PRINTED,
    properties: {
      [AMPLITUDE_EVENT_PROPERTY_FORM_ID]: formId,
      [AMPLITUDE_EVENT_PROPERTY_ENTRY_ID]: entryId,
      [AMPLITUDE_EVENT_PROPERTY_ENTRY_MANAGER_VERSION]: 'Classic Forms',
      [AMPLITUDE_EVENT_PROPERTY_NUM_ENTRIES]: numOfEntries,
      [AMPLITUDE_EVENT_PROPERTY_NUM_QUESTIONS]: numOfQuestions,
    }
  }
}

export const getFormManagerLaunchedEventDetails = (forms, onLogin) => {
  const { queryParams, totalCount } = forms
  const { sortKey, sortDirection, filterKey, pageSize, query } = queryParams || {}

  const props = {
    [AMPLITUDE_EVENT_PROPERTY_SORT_BY_PROP]: sortKey,
    [AMPLITUDE_EVENT_PROPERTY_SORT_ORDER_PROP]: sortDirection,
    [AMPLITUDE_EVENT_PROPERTY_FILTER_PRE_APPLIED]: filterKey !== 'all',
    [AMPLITUDE_EVENT_PROPERTY_FORM_TYPE_VIEW]: 'Legacy Forms',
    [AMPLITUDE_EVENT_PROPERTY_SEARCH_PRE_APPLIED]: query ? true : false,
    [AMPLITUDE_EVENT_PROPERTY_FORMS_PER_PAGE]: pageSize,
    [AMPLITUDE_EVENT_PROPERTY_TOTAL_NUM_FORMS]: totalCount,
    [AMPLITUDE_EVENT_PROPERTY_ON_LOGIN]: onLogin
  }
  return { event: AMPLITUDE_EVENT_FORM_MANAGER_LAUNCHED, properties: props }
}

export const getFormRenameEventDetails = (formId) => {
  return {
    event: AMPLITUDE_EVENT_FORM_MANAGER_FORM_RENAME,
    properties: {
      [AMPLITUDE_EVENT_PROPERTY_FORM_ID]: formId,
      [AMPLITUDE_EVENT_PROPERTY_FORM_TYPE]: 'Legacy Forms'
    }
  }
}

export const getLaunchFBEventDetails = (location, { form, createFormType } = {}) => {
  let purpose

  switch (location) {
    case LAUNCH_LEGACY_LOCATION_CREATE:
      purpose = 'Create New'
      break
    case LAUNCH_LEGACY_LOCATION_EDIT:
    case LAUNCH_LEGACY_LOCATION_FORMNAME:
      purpose = 'Edit'
      break
    default:
      purpose = ''
      break
  }

  const props = {
    [AMPLITUDE_EVENT_PROPERTY_ACTION_INVOKED_LOCATION]: location,
    [AMPLITUDE_EVENT_PROPERTY_PURPOSE]: purpose,
    [AMPLITUDE_EVENT_PROPERTY_FORM_ID]: form?.id || '',
    [AMPLITUDE_EVENT_PROPERTY_FORM_TYPE]: 'Legacy Form Builder',
    [AMPLITUDE_EVENT_PROPERTY_PASSWORD_REQUIRED]: form?.password ? true : false,
    [AMPLITUDE_EVENT_PROPERTY_CREATE_FORM]: createFormType || ''
  }

  return { event: AMPLITUDE_EVENT_FORM_BUILDER_LAUNCHED, properties: props }
}

export const getQueryParamsEventDetails = (queryDiff, queryObj, data) => {

  const { query, labelIds, filterKey, sortKey, sortDirection, page, pageSize } = queryObj
  const { countLimit, totalCount } = data

  // for the case where on page 2+ and changing search, filters, or sort, we shouldn't count the page change to 1 a page turned event
  const queryDiffKeys = Object.keys(queryDiff)
  if (queryDiffKeys.length > 1 && queryDiffKeys.includes('page')) delete queryDiff.page

  let event
  let props
  let amplitudeData = []
  const keys = Object.keys(queryDiff)
  keys.forEach(key => {
    const hasFilter = labelIds.length > 0 || filterKey !== 'all'
    switch (key) {
      case 'query':
        event = AMPLITUDE_EVENT_FORM_MANAGER_SEARCH
        props = {
          [AMPLITUDE_EVENT_PROPERTY_SEARCH_TYPE]: query ? 'Terms' : 'Reset',
          [AMPLITUDE_EVENT_PROPERTY_FORM_TYPE]: 'Legacy Forms',
          [AMPLITUDE_EVENT_PROPERTY_FORMS_PER_PAGE]: pageSize,
          [AMPLITUDE_EVENT_PROPERTY_NUM_FORMS_RETURNED]: totalCount,
          [AMPLITUDE_EVENT_PROPERTY_HAS_FILTER]: hasFilter
        }
        break
      case 'labelIds':
        event = AMPLITUDE_EVENT_FORM_MANAGER_FILTER_BY_LABELS
        props = {
          [AMPLITUDE_EVENT_PROPERTY_NUM_LABELS_APPLIED]: labelIds.length,
          [AMPLITUDE_EVENT_PROPERTY_NUM_FORMS_RETURNED]: totalCount,
          [AMPLITUDE_EVENT_PROPERTY_FORM_TYPE]: 'Legacy Forms',
          [AMPLITUDE_EVENT_PROPERTY_SEARCH_APPLIED]: query ? true : false,
          [AMPLITUDE_EVENT_PROPERTY_VISIBILITY]: filterKey,
        }
        break
      case 'sortKey':
      case 'sortDirection':
        event = AMPLITUDE_EVENT_FORM_MANAGER_SORT
        props = {
          [AMPLITUDE_EVENT_PROPERTY_FORM_TYPE]: 'Legacy Forms',
          [AMPLITUDE_EVENT_PROPERTY_FORMS_PER_PAGE]: pageSize,
          [AMPLITUDE_EVENT_PROPERTY_TOTAL_NUM_FORMS]: totalCount,
          [AMPLITUDE_EVENT_PROPERTY_OLD_SORT_BY]: key === 'sortKey' ? queryDiff.sortKey : sortKey,
          [AMPLITUDE_EVENT_PROPERTY_NEW_SORT_BY]: sortKey,
          [AMPLITUDE_EVENT_PROPERTY_OLD_SORT_ORDER]: key === 'sortDirection' ? queryDiff.sortDirection : sortDirection,
          [AMPLITUDE_EVENT_PROPERTY_NEW_SORT_ORDER]: sortDirection
        }
        break
      case 'page':
        event = AMPLITUDE_EVENT_FORM_MANAGER_PAGE_TURNED
        props = {
          [AMPLITUDE_EVENT_PROPERTY_PAGE_TURNED_TYPE]: queryDiff.page < page ? 'Next' : 'Previous',
          [AMPLITUDE_EVENT_PROPERTY_FORM_TYPE]: 'Legacy Forms',
          [AMPLITUDE_EVENT_PROPERTY_FORMS_PER_PAGE]: pageSize,
          [AMPLITUDE_EVENT_PROPERTY_NUM_FORMS_RETURNED]: totalCount,
          [AMPLITUDE_EVENT_PROPERTY_HAS_FILTER]: hasFilter,
          [AMPLITUDE_EVENT_PROPERTY_NUM_PAGES]: totalCount % pageSize === 0 ? totalCount / pageSize : Math.trunc((totalCount / pageSize) + 1)
        }
        break
      default:
        break
    }
    if (event) amplitudeData.push({ 'event': event, 'properties': { ...props, [AMPLITUDE_EVENT_PROPERTY_TOTAL_NUM_FORMS_IN_ACCOUNT]: countLimit }})
  })
  return amplitudeData
}

export const getUpgradeCTAClickedEventDetails = (upgradeTextKey, location) => {
  return {
    event: AMPLITUDE_EVENT_UPGRADE_CTA_CLICKED,
    properties: {
      [AMPLITUDE_EVENT_PROPERTY_CTA_NAME]: upgradeTextKey,
      [AMPLITUDE_EVENT_PROPERTY_ACTION_INVOKED_LOCATION]: location
    }
  }
}

export const getViewFormLaunchedEventDetails = (formId) => {
  return {
    event: AMPLITUDE_EVENT_FORM_MANAGER_VIEW_FORM,
    properties: {
      [AMPLITUDE_EVENT_PROPERTY_FORM_ID]: formId,
      [AMPLITUDE_EVENT_PROPERTY_FORM_TYPE]: 'Legacy Forms'
    }
  }
}

